import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Calendar, Clock, MapPin, Sun, Home as HomeIcon, ArrowRight } from 'lucide-react';

const BOOKING_URL = "https://link.sweatsetter.com/widget/booking/0bvqp14e0y6pNvAdcqsM";
const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_3bc90ea7-8a21-4aef-876a-f2085af8729d/artifacts/en42xbic_cemetery-throwdowns.jpg";

const classTypes = ['All Classes', 'Large Group Indoor', 'Large Group Outdoor', 'Large Group Run', 'Yoga'];

const scheduleData = [
  { time: '5:45 AM', monday: { type: 'indoor', name: 'LARGE GROUP', subname: 'INDOOR' }, tuesday: { type: 'indoor', name: 'LARGE GROUP', subname: 'INDOOR' }, wednesday: { type: 'indoor', name: 'LARGE GROUP', subname: 'INDOOR' }, thursday: { type: 'indoor', name: 'LARGE GROUP', subname: 'INDOOR' }, friday: { type: 'indoor', name: 'LARGE GROUP', subname: 'INDOOR' }, saturday: null, sunday: null },
  { time: '5:45 AM', monday: null, tuesday: { type: 'outdoor', name: 'LARGE GROUP', subname: 'OUTDOOR' }, wednesday: { type: 'outdoor', name: 'LARGE GROUP', subname: 'OUTDOOR' }, thursday: { type: 'outdoor', name: 'LARGE GROUP', subname: 'OUTDOOR' }, friday: null, saturday: null, sunday: null },
  { time: '7:15 AM', monday: { type: 'indoor', name: 'LARGE GROUP', subname: 'INDOOR' }, tuesday: { type: 'indoor', name: 'LARGE GROUP', subname: 'INDOOR' }, wednesday: { type: 'indoor', name: 'LARGE GROUP', subname: 'INDOOR' }, thursday: { type: 'indoor', name: 'LARGE GROUP', subname: 'INDOOR' }, friday: { type: 'indoor', name: 'LARGE GROUP', subname: 'INDOOR' }, saturday: null, sunday: null },
  { time: '7:30 AM', monday: null, tuesday: null, wednesday: null, thursday: null, friday: null, saturday: { type: 'run', name: 'LARGE GROUP', subname: 'RUN' }, sunday: null },
  { time: '7:30 AM', monday: null, tuesday: null, wednesday: null, thursday: null, friday: null, saturday: { type: 'indoor', name: 'LARGE GROUP', subname: 'INDOOR' }, sunday: null },
  { time: '9:00 AM', monday: null, tuesday: null, wednesday: null, thursday: null, friday: null, saturday: { type: 'yoga', name: 'YOGA', subname: null }, sunday: null },
  { time: '5:30 PM', monday: { type: 'indoor', name: 'LARGE GROUP', subname: 'INDOOR' }, tuesday: { type: 'outdoor', name: 'LARGE GROUP', subname: 'OUTDOOR' }, wednesday: { type: 'indoor', name: 'LARGE GROUP', subname: 'INDOOR' }, thursday: { type: 'outdoor', name: 'LARGE GROUP', subname: 'OUTDOOR' }, friday: { type: 'indoor', name: 'LARGE GROUP', subname: 'INDOOR' }, saturday: null, sunday: null },
  { time: '7:30 PM', monday: null, tuesday: null, wednesday: { type: 'yoga', name: 'YOGA', subname: null }, thursday: null, friday: null, saturday: null, sunday: null },
];

const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const dayLabels = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const getClassStyle = (type) => {
  switch (type) {
    case 'indoor':
      return 'text-cyan-400'; // Cyan blue
    case 'outdoor':
      return 'text-fuchsia-500'; // Magenta
    case 'run':
      return 'text-green-400'; // Bright green
    case 'yoga':
      return 'text-yellow-400'; // Yellow
    default:
      return 'text-white';
  }
};

const getTypeFromFilter = (filter) => {
  switch (filter) {
    case 'Large Group Indoor': return 'indoor';
    case 'Large Group Outdoor': return 'outdoor';
    case 'Large Group Run': return 'run';
    case 'Yoga': return 'yoga';
    default: return null;
  }
};

export default function Schedule() {
  const [activeFilter, setActiveFilter] = useState('All Classes');

  const filteredSchedule = scheduleData.map(row => {
    if (activeFilter === 'All Classes') return row;
    
    const filterType = getTypeFromFilter(activeFilter);
    const filteredRow = { time: row.time };
    
    days.forEach(day => {
      if (row[day] && row[day].type === filterType) {
        filteredRow[day] = row[day];
      } else {
        filteredRow[day] = null;
      }
    });
    
    return filteredRow;
  });

  const hasAnyClasses = filteredSchedule.some(row => 
    days.some(day => row[day] !== null)
  );

  return (
    <div data-testid="schedule-page">
      {/* Hero Section - Full Width Image */}
      <section 
        className="relative flex items-center justify-center"
        style={{ height: '735px', marginTop: '0' }}
        data-testid="schedule-hero"
        aria-label="Schedule hero"
      >
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url(${HERO_IMAGE})`,
            top: '135px',
            height: '600px'
          }}
        >
        </div>
        
        {/* Hero Content */}
        <div className="relative z-10 container-custom text-center" style={{ marginTop: '135px' }}>
          <div className="max-w-4xl mx-auto">
            {/* Text overlay box with semi-transparent background */}
            <div className="bg-black/30 p-[50px] inline-block">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading text-white mb-6">
                Find a Time That Fits Your Life
              </h1>
              <p className="text-lg text-white/90 mb-8">
                With morning and evening options, indoor and outdoor workouts, and multiple training formats, it is easier to stay consistent and build momentum.
              </p>
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                <Button 
                  data-testid="schedule-hero-cta"
                  className="bg-[#FF5A3C] hover:bg-[#E64526] text-white rounded-none font-nav uppercase tracking-widest px-8 py-4 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                >
                  Book a Free Intro
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Schedule Intro */}
      <section className="py-12 bg-[#FF5A3C]" data-testid="schedule-intro" aria-label="Schedule introduction">
        <div className="container-custom">
          <p className="text-center text-lg text-white max-w-3xl mx-auto">
            Whether you prefer early mornings, evenings, indoor training, outdoor workouts, or performance-focused sessions like HYROX, X-Team offers a schedule designed to work with real life.
          </p>
        </div>
      </section>

      {/* Schedule Table */}
      <section className="section-padding bg-[#F5F7FA]" data-testid="schedule-table-section" aria-label="Weekly schedule">
        <div className="container-custom">
          <div className="text-center mb-8">
            <h2 className="text-3xl sm:text-4xl font-heading text-[#1A1A1A] mb-4 uppercase">
              Weekly Training Options
            </h2>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap justify-center gap-[30px] mb-8" data-testid="schedule-filters">
            {classTypes.map((type) => (
              <button
                key={type}
                onClick={() => setActiveFilter(type)}
                data-testid={`filter-${type.toLowerCase().replace(/\s+/g, '-')}`}
                className={`px-6 py-2 rounded-[6px] font-heading text-lg uppercase transition-all duration-300 ${
                  activeFilter === type
                    ? 'bg-[#FF5A3C] text-white shadow-lg'
                    : 'bg-white text-gray-600 border border-gray-200 hover:border-[#FF5A3C] hover:text-[#FF5A3C]'
                }`}
              >
                {type}
              </button>
            ))}
          </div>

          {/* Schedule Table */}
          <div className="bg-black rounded-none shadow-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[800px]">
                <thead>
                  <tr className="bg-gray-900 text-white border-b border-gray-700">
                    <th scope="col" className="py-4 px-4 text-left font-heading text-2xl uppercase">Time</th>
                    {dayLabels.map((day) => (
                      <th scope="col" key={day} className="py-4 px-4 text-center font-heading text-2xl uppercase">{day}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredSchedule.map((row, i) => (
                    <tr key={i} className="border-b border-gray-700 last:border-b-0">
                      <td scope="row" className="py-4 px-4 font-heading text-2xl text-white uppercase whitespace-nowrap">{row.time}</td>
                      {days.map((day) => (
                        <td key={day} className="py-4 px-4 text-center">
                          {row[day] ? (
                            <div className={`font-heading text-2xl uppercase text-center leading-tight ${getClassStyle(row[day].type)}`}>
                              <div>{row[day].name}</div>
                              {row[day].subname && <div>{row[day].subname}</div>}
                            </div>
                          ) : null}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {!hasAnyClasses && (
              <div className="py-12 text-center text-gray-400 font-heading uppercase">
                No classes match the selected filter.
              </div>
            )}
          </div>

          {/* Legend */}
          <div className="flex flex-wrap justify-center gap-6 mt-8">
            <div className="flex items-center gap-2">
              <span className="w-4 h-4 rounded-none bg-cyan-400"></span>
              <span className="text-sm text-gray-600 font-heading uppercase">Indoor</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-4 h-4 rounded-none bg-fuchsia-500"></span>
              <span className="text-sm text-gray-600 font-heading uppercase">Outdoor</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-4 h-4 rounded-none bg-green-400"></span>
              <span className="text-sm text-gray-600 font-heading uppercase">Run</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-4 h-4 rounded-none bg-yellow-400"></span>
              <span className="text-sm text-gray-600 font-heading uppercase">Yoga</span>
            </div>
          </div>
        </div>
      </section>

      {/* Help Choosing */}
      <section className="section-padding bg-white" data-testid="help-choosing" aria-label="Help choosing a program">
        <div className="container-custom">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-heading text-[#1A1A1A] mb-4">
              Not Sure Which Option is Right for You?
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              Start with a Free Intro. We will help you choose the best class, program, and starting point based on your goals, fitness level, and schedule.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                <Button 
                  data-testid="help-choosing-cta"
                  className="w-full sm:w-auto bg-[#FF5A3C] hover:bg-[#E64526] text-white rounded-none font-nav uppercase tracking-widest px-8 py-4 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                >
                  Book a Free Intro
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Schedule Benefits */}
      <section className="section-padding bg-[#F5F7FA]" data-testid="schedule-benefits" aria-label="Schedule benefits">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-heading text-[#1A1A1A] mb-4">
              Built to Fit Real Life
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-none p-8 shadow-sm text-center">
              <div className="w-14 h-14 rounded-none bg-gray-100 flex items-center justify-center mx-auto mb-6">
                <Clock className="w-7 h-7 text-[#FF5A3C]" />
              </div>
              <h3 className="font-heading text-xl text-[#1A1A1A] mb-3">Morning and Evening Options</h3>
              <p className="text-gray-600">Train before work, after work, or wherever your day allows.</p>
            </div>
            
            <div className="bg-white rounded-none p-8 shadow-sm text-center">
              <div className="w-14 h-14 rounded-none bg-gray-100 flex items-center justify-center mx-auto mb-6">
                <Sun className="w-7 h-7 text-[#FF5A3C]" />
              </div>
              <h3 className="font-heading text-xl text-[#1A1A1A] mb-3">Indoor and Outdoor Variety</h3>
              <p className="text-gray-600">Choose from indoor classes, outdoor sessions, and specialty formats.</p>
            </div>
            
            <div className="bg-white rounded-none p-8 shadow-sm text-center">
              <div className="w-14 h-14 rounded-none bg-gray-100 flex items-center justify-center mx-auto mb-6">
                <MapPin className="w-7 h-7 text-[#FF5A3C]" />
              </div>
              <h3 className="font-heading text-xl text-[#1A1A1A] mb-3">Options for Different Goals</h3>
              <p className="text-gray-600">Whether you want general fitness, accountability, or HYROX prep, there is a path for you.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section-padding bg-gradient-to-br from-[#FF5A3C] to-[#E64526] text-white" data-testid="schedule-final-cta" aria-label="Call to action">
        <div className="container-custom text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading mb-6">
            Make Your Schedule Work for Your Goals
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            You do not need a perfect week. You just need a plan you can stick with.
          </p>
          <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
            <Button 
              data-testid="schedule-final-cta-btn"
              className="bg-white text-[#FF5A3C] hover:bg-gray-100 rounded-none font-nav uppercase tracking-widest px-10 py-6 text-lg shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1"
            >
              Book a Free Intro <Calendar className="w-5 h-5 ml-2" />
            </Button>
          </a>
        </div>
      </section>
    </div>
  );
}
